import { useState, useRef } from 'react';
import Select from 'react-select';
import { HashProvider } from '../../Hooks/UseHash';
import useMobileVh from '../../Hooks/UseMobileVh';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import PhotoCard from './PhotoCard';
import BatchActionsBar from './BatchActionsBar';
import ViewPhotoModal from './ViewPhotoModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { usePhotoSelection } from './usePhotoSelection';
import { usePhotoModals } from './usePhotoModals';
import usePhotos, { Photo } from '../../Hooks/UsePhotos';
import { ErrorHandler } from '../../Services/UserApi';
import useHash from '../../Hooks/UseHash';

interface PhotosProps {
	hash: string;
}

interface UploadFile {
	file: File;
	id: string;
	progress: number;
	status: 'pending' | 'uploading' | 'success' | 'error';
	error?: string;
	preview?: string; // Data URL for image preview
}

export default function Photos(props: PhotosProps) {
	const { hash } = props;
	useMobileVh();

	return (
		<HashProvider hash={hash}>
			<div className="photos-container container">
				<div className="row">
					<div className="photos-main-container col-xs-12">
						<PhotosContent />
					</div>
				</div>
			</div>
			<Toaster position="bottom-right" />
		</HashProvider>
	);
}

function PhotosContent() {
	const hash = useHash();
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [sortBy, setSortBy] = useState({ value: 'date-desc', label: 'Date (Newest first)' });
	const fileInputRef = useRef<HTMLInputElement>(null);
	const cameraInputRef = useRef<HTMLInputElement>(null);

	const { photos, isLoading, deletePhoto, deleteMultiplePhotos, uploadPhoto } = usePhotos();
	const { selectedPhotos, isSelectMode, togglePhotoSelection, selectAllPhotos, clearSelection } = usePhotoSelection();

	const {
		modals,
		photoToDelete,
		currentPhoto,
		openViewPhoto,
		closeViewPhoto,
		openDeleteConfirm,
		closeDeleteConfirm,
		openBatchDeleteConfirm,
		closeBatchDeleteConfirm,
	} = usePhotoModals();

	// Photo management functions
	const handleDelete = async () => {
		if (!photoToDelete || isDeleting) return;

		setIsDeleting(true);
		try {
			await deletePhoto(photoToDelete.clientUploadId);
			toast.success('Photo deleted successfully');
			closeDeleteConfirm();
		} catch (error) {
			ErrorHandler.toast(error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleBatchDelete = async () => {
		if (selectedPhotos.size === 0 || isDeleting) return;

		setIsDeleting(true);
		try {
			const photoIds = Array.from(selectedPhotos);
			const result = await deleteMultiplePhotos(photoIds);
			toast.success(result.message || 'Photos deleted successfully');
			clearSelection();
			closeBatchDeleteConfirm();
		} catch (error) {
			ErrorHandler.toast(error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDownload = (photo: Photo) => {
		const link = document.createElement('a');
		link.href = `/u/photos/image/${hash}/${photo.clientUploadFilename}`;
		link.download = photo.clientUploadFilename;
		link.target = '_blank';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	// Upload functionality
	const supportedFormats = ['jpeg', 'jpg', 'png', 'bmp', 'gif', 'webp', 'heic', 'heif', 'tiff', 'tif'];
	const maxFileSize = 10 * 1024 * 1024; // 10MB

	const validateFile = (file: File): string | null => {
		// Check file size first
		if (file.size > maxFileSize) {
			return 'File size cannot exceed 10MB';
		}

		// Check MIME type if available
		if (file.type && file.type.startsWith('image/')) {
			return null;
		}

		// Fallback to file extension check (for HEIC files that have empty MIME type)
		const fileName = file.name.toLowerCase();
		const allowedExtensions = supportedFormats;
		const fileExtension = fileName.split('.').pop();

		if (fileExtension && allowedExtensions.includes(fileExtension)) {
			return null;
		}

		return `File type not supported. Allowed: ${allowedExtensions.join(', ')}`;
	};

	const processFiles = (files: FileList | File[]) => {
		const newUploadFiles: UploadFile[] = [];

		Array.from(files).forEach((file) => {
			const error = validateFile(file);
			const uploadFile: UploadFile = {
				file,
				id: Math.random().toString(36).substring(2, 11),
				progress: 0,
				status: error ? 'error' : 'pending',
				error: error || undefined,
			};

			// Check if this might be an image file
			const fileName = file.name.toLowerCase();
			const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif'];
			const fileExtension = fileName.split('.').pop();
			const isLikelyImage =
				file.type.startsWith('image/') || (fileExtension && imageExtensions.includes(fileExtension));

			// Generate preview for image files
			if (isLikelyImage) {
				// Check if this is a widely supported format
				const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
				const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

				// Consider it widely supported if either MIME type matches OR file extension matches
				const isWidelySupported =
					supportedTypes.includes(file.type.toLowerCase()) ||
					(fileExtension && supportedExtensions.includes(fileExtension));

				// For widely supported formats, generate preview directly
				if (isWidelySupported) {
					const reader = new FileReader();
					reader.onload = (e) => {
						if (e.target?.result) {
							setUploadFiles((prev) =>
								prev.map((f) =>
									f.id === uploadFile.id ? { ...f, preview: e.target!.result as string } : f,
								),
							);
						}
					};
					reader.readAsDataURL(file);
				} else {
					// For unsupported formats (HEIC, etc.), try to generate preview but with fallback
					const reader = new FileReader();
					reader.onload = (e) => {
						if (e.target?.result) {
							const dataUrl = e.target.result as string;

							// Test if the browser can actually display this image
							const testImg = new Image();
							testImg.onload = () => {
								// Success - browser can display this format
								setUploadFiles((prev) =>
									prev.map((f) => (f.id === uploadFile.id ? { ...f, preview: dataUrl } : f)),
								);
							};
							testImg.onerror = () => {
								// Failed - browser can't display this format, use fallback
								setUploadFiles((prev) =>
									prev.map((f) => (f.id === uploadFile.id ? { ...f, preview: 'fallback' } : f)),
								);
							};
							testImg.src = dataUrl;
						}
					};
					reader.onerror = () => {
						// FileReader failed, use fallback
						setUploadFiles((prev) =>
							prev.map((f) => (f.id === uploadFile.id ? { ...f, preview: 'fallback' } : f)),
						);
					};
					reader.readAsDataURL(file);
				}
			}

			newUploadFiles.push(uploadFile);
		});

		setUploadFiles((prev) => [...prev, ...newUploadFiles]);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		processFiles(e.dataTransfer.files);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			processFiles(e.target.files);
		}
	};

	const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			processFiles(e.target.files);
		}
	};

	const openCamera = () => {
		if (cameraInputRef.current) {
			cameraInputRef.current.click();
		}
	};

	const removeFile = (id: string) => {
		setUploadFiles((prev) => prev.filter((f) => f.id !== id));
	};

	const handleUpload = async () => {
		setIsUploading(true);
		const validFiles = uploadFiles.filter((f) => f.status === 'pending');

		for (const uploadFile of validFiles) {
			try {
				// Update status to uploading
				setUploadFiles((prev) =>
					prev.map((f) => (f.id === uploadFile.id ? { ...f, status: 'uploading', progress: 0 } : f)),
				);

				// Simulate progress updates
				const progressInterval = setInterval(() => {
					setUploadFiles((prev) =>
						prev.map((f) => {
							if (f.id === uploadFile.id && f.progress < 90) {
								return { ...f, progress: f.progress + 10 };
							}
							return f;
						}),
					);
				}, 100);

				// Upload file
				await uploadPhoto(uploadFile.file);

				clearInterval(progressInterval);

				// Update file status to success
				setUploadFiles((prev) =>
					prev.map((f) => (f.id === uploadFile.id ? { ...f, status: 'success', progress: 100 } : f)),
				);

				toast.success(`${uploadFile.file.name} uploaded successfully`);
			} catch (error) {
				setUploadFiles((prev) =>
					prev.map((f) =>
						f.id === uploadFile.id
							? {
									...f,
									status: 'error',
									error: error instanceof Error ? error.message : 'Upload failed. Please try again.',
									progress: 0,
								}
							: f,
					),
				);
			}
		}

		setIsUploading(false);

		// Auto-remove successful uploads after delay
		setTimeout(() => {
			setUploadFiles((prev) => prev.filter((f) => f.status !== 'success'));
		}, 2000);
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	// Sorting options
	const sortOptions = [
		{ value: 'date-desc', label: 'Date (Newest first)' },
		{ value: 'date-asc', label: 'Date (Oldest first)' },
		{ value: 'size-desc', label: 'Size (Largest first)' },
		{ value: 'size-asc', label: 'Size (Smallest first)' },
	];

	// Sort photos based on selected option
	const sortPhotos = (photosToSort: typeof photos) => {
		if (!photosToSort) return [];

		const sorted = [...photosToSort].sort((a, b) => {
			switch (sortBy.value) {
				case 'date-desc':
					return b.clientUploadTime - a.clientUploadTime;
				case 'date-asc':
					return a.clientUploadTime - b.clientUploadTime;
				case 'size-desc':
					return b.clientUploadSize - a.clientUploadSize;
				case 'size-asc':
					return a.clientUploadSize - b.clientUploadSize;
				default:
					return 0;
			}
		});

		return sorted;
	};

	// Sort photos
	const processedPhotos = sortPhotos(photos);

	const hasValidFiles = uploadFiles.some((f) => f.status === 'pending');
	const hasFiles = uploadFiles.length > 0;

	if (isLoading) {
		return (
			<div className="photos-loading-container">
				<i className="fas fa-4x fa-images fa-fw m-3"></i>
				<h5 className="mb-3 font-weight-bold">Loading Photos</h5>
				<div>
					<i className="fas fa-spinner fa-pulse fa-fw fa-2x"></i>
				</div>
			</div>
		);
	}

	return (
		<div className="photos-content">
			<div className="photos-header">
				<h2 className="photos-title">Your Photos</h2>
				{photos && photos.length > 0 && (
					<div className="photos-sort">
						<Select
							value={sortBy}
							onChange={(option) => option && setSortBy(option)}
							options={sortOptions}
							isSearchable={false}
							className="photos-sort-select"
							classNamePrefix="photos-sort"
							placeholder="Sort by..."
						/>
					</div>
				)}
			</div>

			{/* Upload Section */}
			<div
				className={`photos-upload-zone ${isDragging ? 'dragging' : ''} ${hasFiles ? 'has-files' : ''}`}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onClick={() => {
					// Only trigger file selection on desktop or when there are files
					// On mobile without files, use the explicit buttons instead
					const isMobile = window.innerWidth <= 768;
					if (!isMobile || hasFiles) {
						fileInputRef.current?.click();
					}
				}}
			>
				{!hasFiles ? (
					<div className="upload-prompt">
						<i className="fas fa-cloud-upload-alt fa-2x" style={{ marginBottom: '0.5rem' }}></i>
						<h3>Add Photos</h3>
						<p>Support for JPEG, PNG, HEIC, WebP and more. Max 10MB per file.</p>

						{/* Mobile camera buttons */}
						<div className="mobile-upload-actions" onClick={(e) => e.stopPropagation()}>
							<button className="btn btn-default mobile-upload-btn" onClick={openCamera}>
								<i className="fas fa-camera"></i>
								<span>Take Photo</span>
							</button>
							<button
								className="btn btn-default mobile-upload-btn"
								onClick={() => fileInputRef.current?.click()}
							>
								<i className="fas fa-folder-open"></i>
								<span>Choose Files</span>
							</button>
						</div>
					</div>
				) : (
					<div className="file-list">
						{uploadFiles.map((uploadFile) => (
							<div key={uploadFile.id} className={`file-item status-${uploadFile.status}`}>
								<div className="file-info">
									{uploadFile.preview === 'fallback' ? (
										<div className="file-preview-fallback">
											<i className="fas fa-file-image"></i>
											<span className="file-type-badge">
												{uploadFile.file.name.split('.').pop()?.toUpperCase() || 'IMG'}
											</span>
										</div>
									) : uploadFile.preview ? (
										<img
											src={uploadFile.preview}
											alt={uploadFile.file.name}
											className="file-preview-image"
										/>
									) : (
										<i className="fas fa-image file-placeholder-icon"></i>
									)}
									<div className="file-details">
										<span className="file-name">{uploadFile.file.name}</span>
										<span className="file-size">{formatFileSize(uploadFile.file.size)}</span>
									</div>
								</div>

								<div className="file-status">
									{uploadFile.status === 'pending' && (
										<button
											className="remove-btn"
											onClick={(e) => {
												e.stopPropagation();
												removeFile(uploadFile.id);
											}}
										>
											<i className="fas fa-times"></i>
										</button>
									)}
									{uploadFile.status === 'uploading' && (
										<div className="progress-container">
											<div className="progress-bar">
												<div
													className="progress-fill"
													style={{ width: `${uploadFile.progress}%` }}
												></div>
											</div>
											<span>{uploadFile.progress}%</span>
										</div>
									)}
									{uploadFile.status === 'success' && (
										<i className="fas fa-check-circle success-icon"></i>
									)}
									{uploadFile.status === 'error' && (
										<>
											<div className="error-status">
												<i className="fas fa-exclamation-circle error-icon"></i>
												<span className="error-text">{uploadFile.error}</span>
											</div>
											<button
												className="remove-btn"
												onClick={(e) => {
													e.stopPropagation();
													removeFile(uploadFile.id);
												}}
												title="Remove failed upload"
											>
												<i className="fas fa-times"></i>
											</button>
										</>
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{hasValidFiles && (
				<div className="upload-actions">
					<button className="btn btn-success" onClick={handleUpload} disabled={isUploading}>
						{isUploading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }} />}
						{isUploading
							? 'Uploading...'
							: `Upload ${uploadFiles.filter((f) => f.status === 'pending').length} Photo${uploadFiles.filter((f) => f.status === 'pending').length !== 1 ? 's' : ''}`}
					</button>
					<button className="btn btn-default" onClick={() => setUploadFiles([])} disabled={isUploading}>
						Clear All
					</button>
				</div>
			)}

			<input
				ref={fileInputRef}
				type="file"
				multiple
				accept={supportedFormats.map((ext) => `.${ext}`).join(',')}
				onChange={handleFileSelect}
				style={{ display: 'none' }}
			/>

			<input
				ref={cameraInputRef}
				type="file"
				accept="image/*"
				capture="environment"
				onChange={handleCameraCapture}
				style={{ display: 'none' }}
			/>

			{/* Photos Grid */}
			{photos && photos.length > 0 && (
				<div className="photos-grid">
					{processedPhotos.map((photo) => (
						<PhotoCard
							key={photo.clientUploadId}
							photo={photo}
							hash={hash}
							isSelected={selectedPhotos.has(photo.clientUploadId)}
							isSelectMode={isSelectMode}
							onToggleSelection={togglePhotoSelection}
							onView={() => openViewPhoto(photo)}
							onDownload={() => handleDownload(photo)}
							onDelete={() => openDeleteConfirm(photo)}
						/>
					))}
				</div>
			)}

			{photos && photos.length === 0 && (
				<div className="photos-empty-state">
					<i className="fas fa-4x fa-images fa-fw m-3"></i>
					<h5 className="mb-3 font-weight-bold">No photos yet</h5>
					<p>Upload your first photo using the area above</p>
				</div>
			)}

			{/* Batch Actions */}
			{isSelectMode && processedPhotos && processedPhotos.length > 0 && (
				<BatchActionsBar
					selectedCount={selectedPhotos.size}
					totalCount={processedPhotos.length}
					onSelectAll={() => selectAllPhotos(processedPhotos)}
					onClearSelection={clearSelection}
					onBatchDelete={openBatchDeleteConfirm}
				/>
			)}

			{/* Modals */}
			{modals.viewPhoto && currentPhoto && (
				<ViewPhotoModal
					photo={currentPhoto}
					hash={hash}
					onClose={closeViewPhoto}
					onDownload={() => handleDownload(currentPhoto)}
					onDelete={() => openDeleteConfirm(currentPhoto)}
				/>
			)}

			<ConfirmDeleteModal
				isOpen={modals.confirmDelete}
				isBatchDelete={false}
				isDeleting={isDeleting}
				onConfirm={handleDelete}
				onCancel={closeDeleteConfirm}
			/>

			<ConfirmDeleteModal
				isOpen={modals.confirmBatchDelete}
				isBatchDelete={true}
				selectedCount={selectedPhotos.size}
				isDeleting={isDeleting}
				onConfirm={handleBatchDelete}
				onCancel={closeBatchDeleteConfirm}
			/>
		</div>
	);
}
